import type { ModerationPolicyCategory } from "shared/Classification";
import { MODERATION_POLICY_CATEGORIES } from "shared/ModerationCategories";
import { useExtracted } from "#i18n";
import { DOCS_NAVIGATION } from "./DocsNavigation";
import { parsePortalPath } from "./PortalPaths";

export interface TopbarBreadcrumb {
  href?: string;
  label: string;
}

const DOCS_SEGMENT_LABELS: Record<string, string> = {
  api: "API Reference",
  cdn: "CDN",
  guides: "Guides",
  lenses: "Lenses & Transformation",
  sdk: "SDKs",
  javascript: "JavaScript SDK",
  authentication: "Authentication",
  "asset-lifecycle": "Asset Lifecycle",
  "pagination-and-filtering": "Pagination & filtering",
  "error-handling": "Error handling",
  "moderation-categories": "Category Definitions",
  "classification-results": "Report Format",
  "moderation-policies": "Policies & Actions",
  webhooks: "Webhooks",
  connectors: "Webhooks & Connectors",
};

const formatSegment = (segment: string) => {
  return (
    DOCS_SEGMENT_LABELS[segment] ??
    segment
      .split("-")
      .filter(Boolean)
      .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
      .join(" ")
  );
};

export const getDocsBreadcrumbs = (pathname: string): TopbarBreadcrumb[] => {
  const group = DOCS_NAVIGATION.find((group) =>
    group.pages.some((page) => page.route === pathname),
  );
  const page = group?.pages.find((page) => page.route === pathname);
  if (group && page)
    return [
      { href: "/docs", label: "Documentation" },
      { label: group.title },
      { label: page.title },
    ];
  const segments = pathname.split("/").filter(Boolean).slice(1);
  if (segments.length === 0) {
    return [{ label: "Documentation" }];
  }

  const breadcrumbs: TopbarBreadcrumb[] = [{ href: "/docs", label: "Documentation" }];
  let href = "/docs";
  segments.forEach((segment, index) => {
    href = `${href}/${segment}`;
    breadcrumbs.push({
      href: index < segments.length - 1 ? href : undefined,
      label: formatSegment(segment),
    });
  });
  return breadcrumbs;
};

/**
 * The topbar's scope trail for a portal page: each level the page sits in, deepest last, every one
 * a switcher among its siblings (PortalTopbar). Pages are named by their own headings, so the trail
 * holds no plain page crumbs; pages outside a scope have none.
 */
export type TopbarScope =
  | { kind: "organisation" }
  | { kind: "project" }
  | { kind: "policy"; policyId: string; category?: ModerationPolicyCategory }
  | { kind: "category"; policyId: string; category: ModerationPolicyCategory }
  | { kind: "asset"; assetId: string }
  | { kind: "hashList"; hashListId: string }
  | { kind: "lens"; identifier: string };

/**
 * Every portal page that is one item of a set (a route with a parameter below its scope), by route
 * ID, and the switcher its trail ends with, so each can switch to its siblings. `null` marks a
 * page with no siblings to switch to, and why. `TopbarBreadcrumbs.test.ts` fails for a new detail
 * route missing here.
 */
export const DETAIL_ROUTE_SWITCHERS: Record<string, TopbarScope["kind"] | null> = {
  "/_authed/portal/proj_{$projectKey}/assets/$assetId": "asset",
  "/_authed/portal/proj_{$projectKey}/hash-lists/$hashListId": "hashList",
  "/_authed/portal/proj_{$projectKey}/lenses/$identifier": "lens",
  "/_authed/portal/org_{$orgKey}/moderation-policies/$policyId": "policy",
  "/_authed/portal/org_{$orgKey}/moderation-policies/$policyId/": "policy",
  "/_authed/portal/org_{$orgKey}/moderation-policies/$policyId/$category": "category",
};

export const getTopbarScopes = (pathname: string): TopbarScope[] => {
  const parsed = parsePortalPath(pathname);
  if (parsed.kind === "project") {
    const [section, id] = parsed.rest.split("/").filter(Boolean).map(decodeURIComponent);
    const scopes: TopbarScope[] = [{ kind: "organisation" }, { kind: "project" }];
    if (!id) return scopes;
    if (section === "assets") return [...scopes, { kind: "asset", assetId: id }];
    if (section === "hash-lists") return [...scopes, { kind: "hashList", hashListId: id }];
    // A new lens has no siblings yet.
    if (section === "lenses" && id !== "new") return [...scopes, { kind: "lens", identifier: id }];
    return scopes;
  }
  if (parsed.kind !== "organisation") return [];
  const [section, policyId, rawCategory] = parsed.rest.split("/").filter(Boolean);
  if (section !== "moderation-policies" || !policyId) return [{ kind: "organisation" }];
  const id = decodeURIComponent(policyId);
  const category = (MODERATION_POLICY_CATEGORIES as readonly string[]).includes(rawCategory ?? "")
    ? (rawCategory as ModerationPolicyCategory)
    : undefined;
  return [
    { kind: "organisation" },
    { kind: "policy", policyId: id, category },
    ...(category ? [{ kind: "category" as const, policyId: id, category }] : []),
  ];
};

/** The topbar's trail on a docs page: the docs section and page, linking back up. */
export const useDocsBreadcrumbs = (pathname: string): TopbarBreadcrumb[] => {
  const translateMessage = useExtracted();
  const translatedLabels: Record<string, string> = {
    "API Reference": translateMessage("API Reference"),
    "Asset Lifecycle": translateMessage("Asset Lifecycle"),
    Authentication: translateMessage("Authentication"),
    CDN: "CDN",
    Documentation: translateMessage("Documentation"),
    "Pagination & filtering": translateMessage("Pagination & filtering"),
    "Error handling": translateMessage("Error handling"),
    Guides: translateMessage("Guides"),
    "JavaScript SDK": translateMessage("JavaScript SDK"),
    SDKs: translateMessage("SDKs"),
    Webhooks: translateMessage("Webhooks"),
    "Webhooks & Connectors": translateMessage("Webhooks & Connectors"),
  };
  if (!pathname.startsWith("/docs")) return [];
  return getDocsBreadcrumbs(pathname).map((breadcrumb) => ({
    ...breadcrumb,
    label: translatedLabels[breadcrumb.label] ?? breadcrumb.label,
  }));
};
